import { useCallback, useEffect, useRef, useState } from "react";
import { TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { card } from "./Notices";

const POLL = 15000;
// Fields the server adds later; unknown until then.
const BLANK = {
  duels: { inbox: 0, free: null, left: null },
  hall: { mayor: null, running: false, ballot: null, closes_at: null },
  notices: [],
};

// The signed-in player's economy: polled while the tab is visible, replaced by every action's response.
// onReady(berths) fires once for each ship that comes in, `clockOffset` keeps timers on server time.
export function useEconomy(active, onReady) {
  const [econ, setEcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [clockOffset, setOffset] = useState(0);
  const live = useRef({ econ: null, offset: 0, seen: new Set() });
  const ready = useRef(onReady);
  ready.current = onReady;

  const set = useCallback((e) => {
    const next = { ...BLANK, ...e };
    live.current.econ = next;
    live.current.offset = Date.parse(e.now) - Date.now();
    setOffset(live.current.offset);
    setEcon(next);
  }, []);

  const refresh = useCallback(() => api("/economy").then((e) => { set(e); setError(false); })
    .catch((e) => setError(![401, 404].includes(e.status))), [set]);

  useEffect(() => {
    if (!active) { live.current.econ = null; setEcon(null); return; }
    setLoading(true);
    refresh().finally(() => setLoading(false));
    const poll = () => document.visibilityState === "visible" && refresh();
    const id = setInterval(poll, POLL);
    document.addEventListener("visibilitychange", poll);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", poll); };
  }, [active, refresh]);

  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      const { econ: e, offset, seen } = live.current;
      if (!e) return;
      const now = Date.now() + offset;
      const fresh = e.berths.filter((b) => b.ship && Date.parse(b.done) <= now && !seen.has(b.done));
      if (!fresh.length) return;
      fresh.forEach((b) => seen.add(b.done));
      ready.current?.(fresh);
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  const act = useCallback(async (path, body, method = "POST") => {
    setBusy(true);
    try {
      const res = await api(path, { method, body });
      const e = res.economy ?? res;
      if (e.berths) set(e);
      return res;
    } catch (err) {
      card({ icon: TriangleAlert, tone: "red", title: err.message }, { duration: 5000 });
      return null;
    } finally {
      setBusy(false);
    }
  }, [set]);

  return { econ, loading, error, busy, refresh, act, clockOffset };
}
